import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/3f757860/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>{`General guidance`}</h2>
    <p><em parentName="p">{`Progress indicator`}</em>{` is a visual representation of a users progress through a set of steps. They guide the user through a number of steps in order to complete a specified process.`}</p>
    <p>{`Use progress indicators to keep the user on track when completing a specific task. By dividing the end goal into smaller, sub-tasks, it increases the percentage of completeness as each task is completed.`}</p>
    <h2>{`Best practices`}</h2>
    <h4>{`Logical progression`}</h4>
    <p>{`Display the steps in order from left to right. Indicate to the user that they are performing a multi-step process, and show the direction of movement. Allow the user to return to a previous step to review their data submission.`}</p>
    <h4>{`Indicate the current step`}</h4>
    <p>{`Keeping the user informed of where they currently are within the process or task at hand will give them a sense of control. This helps the user to know where they are in relation to where they have been, and what sections are to follow. Clear labels should accompany the progress indicator to indicate what the user will accomplish within each step. Keep labels between one to two words.`}</p>
    <h4>{`Validation`}</h4>
    <p>{`Use validation to confirm that a previous step has been completed. If the user cannot proceed onto another step without first completing a task, use an `}<a parentName="p" {...{
        "href": "/components/notification/code#inline-notification"
      }}>{`Inline Notification`}</a>{` to inform them.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "103.80434782608697%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAVCAYAAABG1c6oAAAACXBIWXMAAAsSAAALEgHS3X78AAADMUlEQVQ4y62VXUiTURjHz9nUNKfMoXvfOVvR7KK79EoIvCqSgkZdWBakWWlQ4owuAkt3UWSSBZELUbzyQvTKwE8QvehGIouR0NgmKn7hvnVLc/qe/uedsymZUR748T9nO+//PM95nncj5ICGw+GITmjZBiGMRRcvGCWPmZKUhhNIWYQQD9bdTJFUwZSMMQWgQBkKhZRbc8XExETC9PS0/LjT6STkcOVm1OwdoxnFH3SCqTPLYGEpyZUsV3O+wygW1uqP1HxPf1DbIJqrq452dXVrBgYG1Gaz2dDU1CR4vV6VzWZTcIvJyUlyYMPn80Un/b3vZR3q6aD9/X3Hv9kdBokxkW2EBCZJwo8IE4MrYV0kEhGRorC6uipy+Hx9fV1E+mI4HE7kHlBCAoGAbIgNKQsLC19mZmZCwL24uOhbXl72BYPBvfACP1gCp7gHVEHa2tpkw/b2dtLa2pputVrVdXV1GZ2dnWljY2Opw8PDqpGRkd+RGqfyHUIJiVUImjg1NVWF6Ooxf+J2ux8iQjNSqtmD2HfVQMs9oJTgoVjKSR6Ppx1pDy8tLfUh5UGYDvn9/iFcuKy7GNzSfnCCe0DpgVVZLkh8uRFh8uzsbO/8/PxXRP0JFzy+D3zPZ/ARnNwuSpyhEulemJubu4ZmvYL0r+5DbE8xyOAeUELQ9bJhS0sLHR0dPSZJEu853mN/Bd8LErgH1iS+KLwPx1GMMD6L9dif4L0YQCHc6OVffbhrKP+1KCz2A4MXW1Y08SG73f4UvdiMUxtxty93EFgBoW28v2h0zocNctsE0YfbztFTatfW1lqgb0HzTpab2aYbeKIwD/Z4rOANY9Ma+fl1LyXZ2dly3nq9/mJmZuYrlUpVh6VlJwoLOTtuIaXMQko2LeQ69DIwsXpyiT0nN1iNHNE5BGcwGOR7y8nJeZSbm+szGo12HOLS6XSAq+gSsw2utBKbS13NXOp7Gy71fcmVcEdyKW5JDrBAyqUeuQC3peibkp+f//+vys2tqysoKJA1Ly+PmkwmRVFREUWEVKvVUkEQqChkUY1WT40VNnrmNaOnn23QwgZGU+/i76EMlHO2Iitn5CepqQDFKqob9wAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "Example of the progress indicator in a payment UI",
            "title": "Example of the progress indicator in a payment UI",
            "src": "/static/f06579da45c5ef8fbff38222bf1ee0ab/3cbba/progress-indicator-usage-1.png",
            "srcSet": ["/static/f06579da45c5ef8fbff38222bf1ee0ab/7fc1e/progress-indicator-usage-1.png 288w", "/static/f06579da45c5ef8fbff38222bf1ee0ab/a5df1/progress-indicator-usage-1.png 576w", "/static/f06579da45c5ef8fbff38222bf1ee0ab/3cbba/progress-indicator-usage-1.png 1152w", "/static/f06579da45c5ef8fbff38222bf1ee0ab/392b1/progress-indicator-usage-1.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      